import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import {
  School,
  AccountBalanceWallet,
  EmojiEvents,
  TrendingUp,
  Star,
  People,
  CardMembership,
  AccessTime,
  ArrowForward,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('home');

  // 统计数据
  const stats = [
    {
      icon: <People color="primary" />,
      number: '10,000+',
      label: t('home.stats.students'),
    },
    {
      icon: <School color="primary" />,
      number: '500+',
      label: t('home.stats.courses'),
    },
    {
      icon: <CardMembership color="primary" />,
      number: '5,000+',
      label: t('home.stats.certificates'),
    },
    {
      icon: <TrendingUp color="primary" />,
      number: '95%',
      label: t('home.stats.successRate'),
    },
  ];

  // 特色功能
  const features = [
    {
      icon: <School sx={{ fontSize: 60, color: 'primary.main' }} />,
      title: t('home.features.aiEducation.title'),
      description: t('home.features.aiEducation.description'),
    },
    {
      icon: <AccountBalanceWallet sx={{ fontSize: 60, color: 'secondary.main' }} />,
      title: t('home.features.web3Integration.title'),
      description: t('home.features.web3Integration.description'),
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 60, color: 'success.main' }} />,
      title: t('home.features.nftCertificates.title'),
      description: t('home.features.nftCertificates.description'),
    },
  ];

  // 热门课程数据
  const popularCourses = [
    {
      id: 1,
      title: t('home.courses.aiBasics.title'),
      instructor: t('home.courses.aiBasics.instructor'),
      rating: 4.9,
      students: 2543,
      duration: t('home.courses.aiBasics.duration'),
      price: t('home.courses.aiBasics.price'),
      image: 'https://via.placeholder.com/300x200',
      level: t('home.courses.aiBasics.level'),
    },
    {
      id: 2,
      title: t('home.courses.blockchain.title'),
      instructor: t('home.courses.blockchain.instructor'),
      rating: 4.8,
      students: 1867,
      duration: t('home.courses.blockchain.duration'),
      price: t('home.courses.blockchain.price'),
      image: 'https://via.placeholder.com/300x200',
      level: t('home.courses.blockchain.level'),
    },
    {
      id: 3,
      title: t('home.courses.smartContracts.title'),
      instructor: t('home.courses.smartContracts.instructor'),
      rating: 4.7,
      students: 1234,
      duration: t('home.courses.smartContracts.duration'),
      price: t('home.courses.smartContracts.price'),
      image: 'https://via.placeholder.com/300x200',
      level: t('home.courses.smartContracts.level'),
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            {t('home.hero.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.9,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
            }}
          >
            {t('home.hero.subtitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              opacity: 0.8,
              fontSize: { xs: '1rem', md: '1.1rem' },
            }}
          >
            {t('home.hero.description')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('home.hero.startLearning')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/web3"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('home.hero.exploreWeb3')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{stat.icon}</Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            {t('home.features.title')}
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            {t('home.features.subtitle')}
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                  <Box sx={{ mb: 3 }}>{feature.icon}</Box>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Courses Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
          {t('home.popularCourses.title')}
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          {t('home.popularCourses.subtitle')}
        </Typography>

        <Grid container spacing={4}>
          {popularCourses.map((course) => (
            <Grid item xs={12} md={4} key={course.id}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                <Box
                  sx={{
                    height: 200,
                    backgroundImage: `url(${course.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  <Chip
                    label={course.level}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    }}
                  />
                </Box>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {course.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {course.instructor}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Star sx={{ color: 'orange', mr: 0.5, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {course.rating}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({course.students} {t('home.popularCourses.students')})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {course.duration}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {course.price}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    component={Link}
                    to={`/courses/${course.id}`}
                    endIcon={<ArrowForward />}
                  >
                    {t('home.popularCourses.viewCourse')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="outlined"
            size="large"
            component={Link}
            to="/courses"
            endIcon={<ArrowForward />}
          >
            {t('home.popularCourses.viewAll')}
          </Button>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom>
            {t('home.cta.title')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('home.cta.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('home.cta.register')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/login"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 4,
                py: 1.5,
              }}
            >
              {t('home.cta.login')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;